import React, { useState } from 'react';
import { Card, Button, Table, Select, Upload, message, Popconfirm, Tabs } from 'antd';
import { DownloadOutlined, ReloadOutlined, InboxOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import {
  GET_SETTLEMENT_IMPORT_TASKS,
  GET_SETTLEMENT_IMPORT_ERROR_REPORT_URL,
} from './graphql/queries';
import {
  RETRY_SETTLEMENT_IMPORT_TASK,
  CANCEL_SETTLEMENT_IMPORT_TASK,
} from './graphql/mutations';
import type {
  SettlementImportTaskType,
  GetSettlementImportTasksResponse,
  GetSettlementImportErrorReportUrlResponse,
  SettlementImportUploadResponse,
} from './graphql/types';
import { getToken, getUserInfo } from 'utils/auth';
import { getOrCreateDeviceId, getEndType } from 'utils/deviceId';
import SettlementDetailTab from './SettlementDetailTab';

const CUSTOMERS: { label: string; value: string }[] = [
  { label: 'TEMU', value: 'TEMU' },
  { label: 'TEMU 半托', value: 'TEMU_SEMI' },
  { label: 'TIKTOK', value: 'TIKTOK' },
  { label: 'SHEIN', value: 'SHEIN' },
  { label: 'JD', value: 'JD' },
  { label: 'SF', value: 'SF' },
];

const IN_PROGRESS_STATUSES = new Set(['PENDING', 'PARSING', 'ENRICHING']);
const FAILED_STATUSES = new Set(['PARTIAL_FAILED', 'PARSE_FAILED']);

const isTerminal = (status: string) => !IN_PROGRESS_STATUSES.has(status);

// 后端返回 epoch millis 的 Long，GraphQL 序列化为字符串，需先转 Number 再构造 Date，否则 Invalid Date
const formatMillis = (v?: number | string | null): string => {
  if (v === null || v === undefined || v === '') {
    return '—';
  }
  const millis = Number(v);
  return Number.isNaN(millis) ? '—' : new Date(millis).toLocaleString();
};

// 流式 multipart 上传（不走 GraphQL base64），后端落临时盘再流式传 S3，支持 300MB 大文件
const uploadSettlementImportFile = async (
  customer: string,
  file: File,
): Promise<SettlementImportUploadResponse> => {
  const token = getToken();
  const account = getUserInfo()?.account;
  const formData = new FormData();
  formData.append('customer', customer);
  formData.append('file', file, file.name);
  if (account?.id) {
    formData.append('submitterId', account.id);
  }
  if (account?.name) {
    formData.append('submitterName', account.name);
  }
  const response = await fetch('/api/settlement/import/upload', {
    method: 'POST',
    body: formData,
    headers: {
      authorization: token ? `Bearer ${token}` : '',
      DeviceInfo: JSON.stringify({ deviceUUID: getOrCreateDeviceId(), endType: getEndType() }),
    },
  });
  return response.json();
};

const SettlementImportDataPage: React.FC = () => {
  const client = useApolloClient();
  const [customer, setCustomer] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const { data, loading, refetch } = useQuery<GetSettlementImportTasksResponse>(
    GET_SETTLEMENT_IMPORT_TASKS,
    { fetchPolicy: 'cache-and-network' },
  );

  const [retryTask] = useMutation(RETRY_SETTLEMENT_IMPORT_TASK);
  const [cancelTask] = useMutation(CANCEL_SETTLEMENT_IMPORT_TASK);

  const tasks = data?.getSettlementImportTasks ?? [];

  const handleUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      message.error('仅支持 .xlsx 文件');
      return false;
    }
    if (!customer) {
      message.error('请先选择客户');
      return false;
    }
    setUploading(true);
    message.loading({ content: '上传中...', key: 'settlement-import-upload', duration: 0 });
    try {
      const result = await uploadSettlementImportFile(customer, file);
      message.destroy('settlement-import-upload');
      if (result.success) {
        message.success(`任务已创建，taskId=${result.taskId}`);
        refetch();
      } else {
        message.error(`上传失败：${result.message || '未知错误'}`);
      }
    } catch (error: unknown) {
      message.destroy('settlement-import-upload');
      const err = error as Error;
      message.error(`上传失败：${err?.message || '未知错误'}`);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleRetry = async (record: SettlementImportTaskType) => {
    try {
      const res = await retryTask({ variables: { taskId: Number(record.id) } });
      if (res.data?.retrySettlementImportTask) {
        message.success('已重新排队');
      } else {
        message.warning('任务不存在或无法重试');
      }
      refetch();
    } catch (error: unknown) {
      message.error(`重试失败：${(error as Error)?.message || '未知错误'}`);
    }
  };

  const handleCancel = async (record: SettlementImportTaskType) => {
    try {
      const res = await cancelTask({ variables: { taskId: Number(record.id) } });
      if (res.data?.cancelSettlementImportTask) {
        message.success('已取消');
      } else {
        message.warning('任务不存在或非进行中');
      }
      refetch();
    } catch (error: unknown) {
      message.error(`取消失败：${(error as Error)?.message || '未知错误'}`);
    }
  };

  const handleDownloadErrorReport = async (record: SettlementImportTaskType) => {
    try {
      const res = await client.query<GetSettlementImportErrorReportUrlResponse>({
        query: GET_SETTLEMENT_IMPORT_ERROR_REPORT_URL,
        variables: { taskId: Number(record.id) },
        fetchPolicy: 'network-only',
      });
      const url = res.data?.getSettlementImportErrorReportUrl;
      if (url) {
        window.open(url, '_blank');
      } else {
        message.warning('无异常报告文件');
      }
    } catch (error: unknown) {
      message.error(`获取下载链接失败：${(error as Error)?.message || '未知错误'}`);
    }
  };

  const columns: ColumnsType<SettlementImportTaskType> = [
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: '客户', dataIndex: 'customer', key: 'customer', width: 90 },
    {
      title: '源文件',
      dataIndex: 'sourceFileName',
      key: 'sourceFileName',
      width: 200,
      ellipsis: true,
    },
    { title: '提交人', dataIndex: 'submitterName', key: 'submitterName', width: 120, render: (v) => v ?? '—' },
    { title: '状态', dataIndex: 'status', key: 'status', width: 130 },
    {
      title: '进度',
      key: 'progress',
      width: 160,
      render: (_, record) => {
        if (FAILED_STATUSES.has(record.status)) {
          return `${record.successCount}/${record.rowCount} 成功，${record.failCount} 失败`;
        }
        if (record.status === 'COMPLETED') {
          return `${record.successCount}/${record.rowCount} 成功`;
        }
        // 进行中：Excel 流式解析，总行数须解析完才知晓，ENRICHING 阶段仅逐批回写累计成功行数，故展示已处理行数
        if (IN_PROGRESS_STATUSES.has(record.status) && record.successCount > 0) {
          return `已处理 ${record.successCount} 行`;
        }
        return record.failureReason ?? '—';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: number) => formatMillis(v),
    },
    {
      title: '完成时间',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 180,
      render: (v?: number) => formatMillis(v),
    },
    {
      title: '取消时间',
      dataIndex: 'cancelledAt',
      key: 'cancelledAt',
      width: 180,
      render: (v?: number) => formatMillis(v),
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      render: (_, record) => (
        <span className="flex flex-wrap gap-2">
          {FAILED_STATUSES.has(record.status) && (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadErrorReport(record)}
            >
              异常报告
            </Button>
          )}
          {isTerminal(record.status) && (
            <Popconfirm title="确认重试该任务？" onConfirm={() => handleRetry(record)}>
              <Button type="link" size="small">
                重试
              </Button>
            </Popconfirm>
          )}
          {IN_PROGRESS_STATUSES.has(record.status) && (
            <Popconfirm title="确认取消该任务？" onConfirm={() => handleCancel(record)}>
              <Button type="link" size="small" danger>
                取消
              </Button>
            </Popconfirm>
          )}
        </span>
      ),
    },
  ];

  const importTab = (
    <div className="flex flex-col gap-6">
      <Card title="上传设置">
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="whitespace-nowrap font-medium text-gray-700">
              <span className="mr-1 text-red-500">*</span>客户
            </span>
            <Select
              value={customer}
              onChange={setCustomer}
              placeholder="请选择账单所属客户"
              allowClear
              className="w-64"
            >
              {CUSTOMERS.map((c) => (
                <Select.Option key={c.value} value={c.value}>
                  {c.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Upload.Dragger
            accept=".xlsx"
            showUploadList={false}
            beforeUpload={(file) => {
              handleUpload(file);
              return false;
            }}
            disabled={uploading || !customer}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或将 .xlsx 账单文件拖拽到此处上传</p>
            <p className="ant-upload-hint">
              单个文件 · 仅支持 .xlsx · 最大 300MB
              {customer ? ` · 当前客户：${customer}` : ' · 请先在上方选择客户'}
            </p>
          </Upload.Dragger>
        </div>
      </Card>

      <Card
        title="导入任务"
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={loading}>
            刷新
          </Button>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={tasks}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            onChange: (current, pageSize) => setPagination({ current, pageSize }),
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 1800 }}
          locale={{ emptyText: '暂无任务' }}
        />
      </Card>
    </div>
  );

  return (
    <Tabs
      defaultActiveKey="import"
      items={[
        { key: 'import', label: '数据导入', children: importTab },
        { key: 'detail', label: '明细查看', children: <SettlementDetailTab /> },
      ]}
    />
  );
};

export default SettlementImportDataPage;
